/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Map as MaplibreGlMap, StyleSpecification } from 'maplibre-gl';
import { Map as MaplibreMap } from 'react-map-gl/maplibre';
import type { MapRef } from 'react-map-gl/maplibre';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Box, CircularProgress } from '@mui/material';
import DeckGL from '@deck.gl/react';
import type { DeckGLRef } from '@deck.gl/react';
import React, { PropsWithChildren, ReactElement, ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { mapDrawerAtom, mapDrawerWidthAtom } from '../components/MapDrawer';
import type { ViewStateProps } from './deckgl-types';
import type { InteractionState, LayersList, PickingInfo } from '@deck.gl/core';
import type { Feature, FeatureCollection } from 'geojson';
import { MapView, FlyToInterpolator, WebMercatorViewport } from '@deck.gl/core';
import { useRegionLayer } from '../Regions/FemaRegions';
import { useHomeLayers } from '../pages/Home';
import { useIncidentLayers } from '../Incident/incidentHooks';
import { PrimaryLayer } from '../pages/MapPage';
import { useCountyLayer } from '../Incident/CountyLayer';
import type { ClickInfo } from './atoms';
import {
  clickInfoAtom,
  debouncedViewStateAtom,
  initialViewState,
  mapFirstLoadAtom,
  mapInteractingAtom,
  settledViewStateAtom,
  VIEW_STATE_PUBLISH_MS,
  VIEW_STATE_SETTLE_MS,
  viewStateAtom,
  visualModeAtom,
} from './atoms';
import { parseBboxParam } from './bboxParam';
import { basemapSupportsWorldview, buildRasterStyle, getBasemap, mapStyleAtom } from './basemaps';
import { extractAttributions, mapAttributionAtom } from './attribution';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { useLocation, useMatch, useSearchParams } from 'react-router-dom';
import LayerInfoPopup from './layers/LayerInfoPopup';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';
import { useClickedExposureEvent, useStructuresLayer } from './layers/StructuresLayer';
import { useTribalLayer } from './layers/TribalLayer';
import { ToggleLabelsButton } from './ToggleLabelsButton';
import { DownloadButton } from './DownloadButton';
import { NavigationButtons } from './NavigationButtons';
import { fetchBasemapStyle, transformProxyRequest } from '../services/arcgisProxy';
import { ErrorBoundary } from '../components/ErrorBoundary';
import {
  applyLayerOverrides,
  computePresentLeaves,
  flattenLayers,
} from './layerControls/applyLayerOverrides';
import {
  groupOpacityAtom,
  groupVisibilityAtom,
  leafOpacityAtom,
  leafVisibilityAtom,
  presentLeavesAtom,
  swipeAtom,
  swipeEnabledAtom,
  swipeLayersAtom,
} from './layerControls/atoms';
import { SwipeInteraction } from './layerControls/SwipeInteraction';
import { suppressMapContextMenu } from './deckWrapper';

type Props = {
  layers?: LayersList;
  featureCollection?: FeatureCollection;
  outerChildren?: React.ReactElement;
  children?: React.ReactNode;
  selectedMap: PrimaryLayer;
  title: string;
  miniMap?: boolean;
  hideDrawer?: boolean;
  context?: string;
};

/** Screen padding around a `?bbox=` deep link when fitting the camera to it. */
const BBOX_FIT_PADDING = 20;

const Map: React.FC<Props> = props => {
  const [viewState, setViewState] = useAtom(viewStateAtom);
  const [openDrawer] = useAtom(mapDrawerAtom);
  const [mapDrawerWidth] = useAtom(mapDrawerWidthAtom);
  const [mapStyle] = useAtom(mapStyleAtom);
  const [visualMode, setVisualMode] = useAtom(visualModeAtom);
  const [debounceViewState] = useDebounce(viewState, VIEW_STATE_PUBLISH_MS);
  const setDebouncedViewState = useSetAtom(debouncedViewStateAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const [firstLoad, setFirstLoad] = useAtom(mapFirstLoadAtom);
  const incidentPage = useMatch({
    path: '/:incidentId/:sectionId/:viewType/:attributeId?',
  });

  const incidentcastPage = useMatch({
    path: '/wowcast/:incidentId/:sectionId',
  });

  const isIncidentPage = incidentPage != null || incidentcastPage != null;

  const bboxSearchParams = searchParams.get('bbox');
  const isCanopyUrl = searchParams.has('canopy_kml_id');

  const parsedBbox = React.useMemo(() => parseBboxParam(bboxSearchParams), [bboxSearchParams]);

  // The canvas size the camera carries, written by deck's resize events below.
  // The deep-link fit must use it: fitting against the document (or any other
  // size) lands the wrong zoom for a canvas inset by the sidebar and chrome.
  const canvasWidth = viewState.width;
  const canvasHeight = viewState.height;

  const bboxViewState = React.useMemo(() => {
    if (parsedBbox == null) return undefined;
    // Until deck measures the canvas there is nothing to fit into, and a
    // transient near-zero size cannot fit the padding (fitBounds asserts).
    // Waiting is fine: this memo recomputes when the camera gets the size,
    // and the initial-bounds effect fires again with it.
    if (canvasWidth == null || canvasHeight == null) return undefined;
    if (canvasWidth <= BBOX_FIT_PADDING * 2 || canvasHeight <= BBOX_FIT_PADDING * 2) {
      return undefined;
    }
    const [minLng, minLat, maxLng, maxLat] = parsedBbox;
    return new WebMercatorViewport({
      width: canvasWidth,
      height: canvasHeight,
      ...initialViewState,
    }).fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: BBOX_FIT_PADDING }
    );
  }, [parsedBbox, canvasWidth, canvasHeight]);

  const handleInitialBounds = React.useCallback(() => {
    if (bboxViewState != null && firstLoad) {
      // A functional update: the camera keeps the canvas size and limits it
      // carries, and only its position changes.
      setViewState(previous => ({
        ...previous,
        latitude: bboxViewState.latitude,
        longitude: bboxViewState.longitude,
        zoom: bboxViewState.zoom,
        bearing: bboxViewState.bearing,
        transitionDuration: 1000,
        transitionInterpolator: new FlyToInterpolator(),
      }));
      setFirstLoad(false);
    }
  }, [bboxViewState, setViewState, setFirstLoad, firstLoad]);

  React.useEffect(() => {
    handleInitialBounds();
  }, [handleInitialBounds, isIncidentPage]);

  const location = useLocation();

  const handleBbox = React.useCallback(() => {
    const { width, height } = debounceViewState;
    // Until the camera carries the measured canvas there is no truthful
    // viewport to publish.
    if (width == null || height == null) return;
    const viewport = new WebMercatorViewport({ ...debounceViewState, width, height });
    const bbox = viewport.getBounds() as number[];

    if (isCanopyUrl) {
      searchParams.set('canopy_kml_id', searchParams.get('canopy_kml_id') ?? '');
    }
    searchParams.set('bbox', bbox.map(n => n.toFixed(3)).join(','));
    setSearchParams(searchParams, { replace: true });
  }, [debounceViewState, setSearchParams, searchParams, isCanopyUrl]);

  React.useEffect(() => {
    // No honourable bbox in the URL (absent or malformed) means there is no
    // initial fit to wait for. Incident pages frame their own extent and
    // canopy URLs write their swath's bbox, so those keep waiting.
    if (firstLoad && parsedBbox == null && !isIncidentPage && !isCanopyUrl) {
      setFirstLoad(false);
    }

    // Publishing waits for the camera to carry the measured canvas: an
    // unsized camera is not a truthful viewport, for the URL or for the
    // viewport-bounded consumers of the debounced atom.
    if (!firstLoad && debounceViewState.width != null && debounceViewState.height != null) {
      setDebouncedViewState(debounceViewState);
      handleBbox();
    }
  }, [
    isIncidentPage,
    parsedBbox,
    debounceViewState,
    setDebouncedViewState,
    setSearchParams,
    isCanopyUrl,
    handleBbox,
    firstLoad,
    setFirstLoad,
  ]);

  // The size deck.gl measured for its canvas, which is the map: the container it
  // fills is inset by the sidebar, so it is materially smaller than the browser
  // window. Everything derived from the camera reads these dimensions -- the
  // viewport a full-resolution geometry fetch is bounded to, the shareable bbox
  // param, the "filter within view" envelope -- and each of those was answering
  // for the whole document (or, where there was no fallback, for a 1x1 pixel
  // viewport) because the camera carried no size outside the bbox-fit path.
  const [canvasSize, setCanvasSize] = React.useState<{ width: number; height: number } | null>(
    null
  );
  const handleResize = React.useCallback((size: { width: number; height: number }) => {
    setCanvasSize(previous =>
      previous?.width === size.width && previous?.height === size.height
        ? previous
        : { width: size.width, height: size.height }
    );
  }, []);

  const handleViewStateChange = React.useCallback(
    ({ viewState }: { viewState: ViewStateProps }) => {
      // The camera must never lose the size it carries: before deck's first
      // resize event this writer would otherwise blank it on every change.
      setViewState(previous => ({
        ...viewState,
        maxPitch: 85,
        width: canvasSize?.width ?? previous.width,
        height: canvasSize?.height ?? previous.height,
      }));
    },
    [setViewState, canvasSize]
  );

  // A resize alone changes what is on screen, so the camera has to carry the new
  // dimensions without waiting for the user to move it. Returning the camera
  // untouched when they already match keeps this from handing DeckGL a new view
  // state object -- which, mid fly-to, would restart the transition.
  React.useEffect(() => {
    if (canvasSize == null) {
      return;
    }
    setViewState(previous =>
      previous.width === canvasSize.width && previous.height === canvasSize.height
        ? previous
        : { ...previous, width: canvasSize.width, height: canvasSize.height }
    );
  }, [canvasSize, setViewState]);

  // Publishes the camera once it stops moving. Trailing-edge only, so it stays
  // unwritten (null) for as long as the camera keeps changing -- including
  // through the fly-to a route may start on mount.
  const setSettledViewState = useSetAtom(settledViewStateAtom);
  const commitSettledViewState = useDebouncedCallback(
    (settled: ViewStateProps & { width?: number; height?: number }) => setSettledViewState(settled),
    VIEW_STATE_SETTLE_MS
  );

  React.useEffect(() => {
    commitSettledViewState(viewState);
  }, [viewState, commitSettledViewState]);

  const setMapInteracting = useSetAtom(mapInteractingAtom);
  const handleInteractionStateChange = React.useCallback(
    (interactionState: InteractionState) => {
      const { isDragging, isPanning, isZooming, isRotating, inTransition } = interactionState;
      setMapInteracting(
        Boolean(isDragging || isPanning || isZooming || isRotating || inTransition)
      );
    },
    [setMapInteracting]
  );

  const deckRef = React.useRef<DeckGLRef>(null);
  const mapRef = React.useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasPitch = (viewState?.pitch ?? 0) > 20;

  const handleDragEnd = React.useCallback(() => {
    if (hasPitch && visualMode === '2d') {
      setVisualMode('3d');
    } else if (!hasPitch && visualMode === '3d') {
      setVisualMode('2d');
    }
  }, [hasPitch, setVisualMode, visualMode]);

  const containerStyles = React.useMemo(() => {
    if (props.hideDrawer) {
      return {
        height: 'calc(100% - 64px)',
        '@media print': {
          height: '100%',
        },
      };
    }

    return {
      marginLeft: openDrawer ? `${mapDrawerWidth}px` : 0,
      transition: 'margin 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      height: 'calc(100% - 64px)',

      '@media print': {
        marginLeft: 0,
      },
    };
  }, [openDrawer, mapDrawerWidth, props.hideDrawer]);

  const rasterStyle = React.useMemo<StyleSpecification | null>(() => {
    const basemap = getBasemap(mapStyle);
    return basemap?.raster ? buildRasterStyle(basemap.raster) : null;
  }, [mapStyle]);

  const [vectorStyle, setVectorStyle] = React.useState<StyleSpecification | null>(null);

  // Vector styles are fetched as JSON (rather than passed as a URL) so
  // fetchBasemapStyle can absolutize the proxied sprite path -- MapLibre
  // rejects relative sprite URLs before transformRequest runs.
  React.useEffect(() => {
    if (rasterStyle != null) return;
    let cancelled = false;
    fetchBasemapStyle(
      mapStyle,
      basemapSupportsWorldview(mapStyle) ? 'unitedStatesOfAmerica' : undefined
    )
      .then(style => {
        if (!cancelled) setVectorStyle(style);
      })
      .catch((error: unknown) => {
        console.error('Failed to load basemap style:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [mapStyle, rasterStyle]);

  const basemapStyle = rasterStyle ?? vectorStyle;

  const zoomLevel = React.useMemo(
    () => ((debounceViewState?.zoom ?? 0) > 8 ? 'inner' : 'outer'),
    [debounceViewState?.zoom]
  );

  const [clickInfo, setClickInfo] = useAtom(clickInfoAtom);
  // The popup belongs to the map that opened it: none is open when a map mounts.
  React.useEffect(() => () => setClickInfo(null), [setClickInfo]);
  const [activeObjectIndex, setActiveObjectIndex] = React.useState<number>(0);
  const [, setSelectedFeature] = useAtom(selectedFeatureAtom);
  // The popup's active feature, whether just picked or stepped to with
  // prev/next, selects the exposure event the structures views key on.
  const activeObject = clickInfo?.objects[activeObjectIndex];
  const activeFeature = React.useMemo(
    () =>
      activeObject === undefined
        ? undefined
        : { layerId: activeObject.layer.id, properties: activeObject.object.properties },
    [activeObject]
  );
  useClickedExposureEvent(
    activeFeature,
    `${location.pathname}|${props.selectedMap}|${searchParams.get('canopy_kml_id')}`
  );

  function onClick(event: PickingInfo<unknown>) {
    const { x, y, coordinate } = event;

    if (deckRef.current) {
      // deck.gl's suggested replacement, pickObjectsAsync, only returns the
      // topmost object per pixel. pickMultipleObjects depth-peels the stacked
      // features under the click, which the popup's prev/next navigation needs.
      // It is deprecated because it is WebGL-only; this map renders on WebGL.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const pickedObjects = deckRef.current.pickMultipleObjects({
        x,
        y,
        radius: 1,
      });

      // Every pick over the map carries the map coordinate under the pointer;
      // it is the point the popup stays attached to as the camera moves.
      if (pickedObjects.length > 0 && coordinate != null) {
        setClickInfo({
          coordinate: coordinate as ClickInfo['coordinate'],
          objects: pickedObjects
            .filter(info => info.layer != null)
            .map(info => ({
              layer: info.layer!,
              object: info.object as Feature,
            })),
        });
        setActiveObjectIndex(0);
        const feature = pickedObjects[0].object as Feature;
        setSelectedFeature(feature);
      } else {
        setClickInfo(null);
      }
    }
  }

  function handlePrevClick() {
    setActiveObjectIndex(prevIndex => {
      const newIndex = Math.max(prevIndex - 1, 0);
      const obj = clickInfo?.objects[newIndex]?.object;
      setSelectedFeature(obj ?? null);
      return newIndex;
    });
  }

  function handleNextClick() {
    setActiveObjectIndex(prevIndex => {
      const newIndex = Math.min(prevIndex + 1, (clickInfo?.objects.length ?? 0) - 1);
      const obj = clickInfo?.objects[newIndex]?.object;
      setSelectedFeature(obj ?? null);
      return newIndex;
    });
  }

  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const setMapAttribution = useSetAtom(mapAttributionAtom);
  const lastAttributionRef = useRef('');
  const handleStyleData = React.useCallback(
    (event: { target: MaplibreGlMap }) => {
      const attributions = extractAttributions(event.target.getStyle());
      const joined = attributions.join('|');
      if (joined !== lastAttributionRef.current) {
        lastAttributionRef.current = joined;
        setMapAttribution(attributions);
      }
    },
    [setMapAttribution]
  );

  // While swipe mode is on, disable deck's left-drag pan and right-drag
  // rotate so the LEFT drag is free for swiping; scroll-zoom stays on
  // (default) and middle-drag pan is handled by SwipeInteraction. The swipe
  // interaction only mounts on the default map, so the wowcast mini map keeps
  // its normal controls even while the (session-global) swipe mode is on.
  const swipeEnabledValue = useAtomValue(swipeEnabledAtom);
  const swipeEnabled = swipeEnabledValue && props.context !== 'wowcast';

  // The cursor must indicate what LEFT drag does. Deck's default getCursor
  // shows grab/grabbing on its canvas, overriding any container style -- so
  // while swipe mode owns the left drag, tell deck to show a crosshair
  // instead.
  const getCursor = React.useCallback(
    ({ isDragging }: { isDragging: boolean }) =>
      swipeEnabled ? 'crosshair' : isDragging ? 'grabbing' : 'grab',
    [swipeEnabled]
  );

  return (
    <Box className="full-width-height" sx={{ display: 'flex', ...containerStyles }}>
      <Box
        ref={containerRef}
        id="map-container"
        onContextMenu={suppressMapContextMenu}
        className={['map-data-container', props.miniMap ? 'mini-map' : ''].join(' ')}
        sx={{
          position: 'relative',
          display: 'flex',
          width: 1,
          height:
            props.context === 'wowcast'
              ? 'calc(100vh - 228px) !important'
              : 'calc(100vh - 134px) !important',
        }}
      >
        {/* The map is its own failure domain, and the boundary is inside this
            container rather than around the whole component so a failure in the
            layers or the canvas leaves the on-map controls, the stats panel and
            the feature popup -- siblings below -- usable. Every layer's data is
            read during HandleLayers' render, so this is also what contains a
            malformed feed or an unrepresentable atom key. */}
        <ErrorBoundary region="The map" resetKey={`${location.pathname}|${props.selectedMap}`}>
          <HandleLayers
            context={props.context}
            deckRef={deckRef}
            selectedMap={props.selectedMap}
            mapStyle={mapStyle}
            zoomLevel={zoomLevel}
          >
            <LoadingIndicator status="init" />
            <DeckGL
              ref={deckRef}
              controller={swipeEnabled ? { dragPan: false, dragRotate: false } : true}
              getCursor={getCursor}
              layers={[...(props?.layers ?? [])]}
              onViewStateChange={handleViewStateChange}
              onResize={handleResize}
              onInteractionStateChange={handleInteractionStateChange}
              onDragEnd={handleDragEnd}
              views={[new MapView({ repeat: true })]}
              viewState={{ MapView: viewState }}
              onClick={onClick}
              useDevicePixels={false}
              onLoad={() => {
                const clouds = document.getElementById('clouds');
                if (clouds) {
                  clouds.style.visibility = 'visible';
                }
                setIsMapLoaded(true);
              }}
            >
              <MaplibreMap
                ref={mapRef}
                mapStyle={basemapStyle ?? undefined}
                canvasContextAttributes={{ preserveDrawingBuffer: true }}
                // The main map surfaces attribution in the InfoModal instead of an
                // always-on control; wowcast-context pages have no InfoModal, so
                // they keep the on-map control (Esri/OSM attribution requirement).
                attributionControl={
                  props.context === 'wowcast' ? { customAttribution: 'NLT' } : false
                }
                onStyleData={handleStyleData}
                transformRequest={transformProxyRequest}
                maxPitch={85}
              >
                {props.context !== 'wowcast' && (
                  <>
                    <MapButtons
                      miniMap={props.miniMap}
                      context={props.context}
                      title={props.title}
                      portalRef={containerRef}
                      mapLoaded={isMapLoaded}
                      deckRef={deckRef}
                      mapRef={mapRef}
                    />
                    <MapNavigation portalRef={containerRef} />
                  </>
                )}
                {isMapLoaded && props.children}
              </MaplibreMap>
            </DeckGL>
          </HandleLayers>
        </ErrorBoundary>

        {/* The panel layer controls live inside Map Controls (MapControls.tsx);
            only the swipe interaction mounts here, inside the map container,
            so its drag coordinates align with the map's top-left origin. */}
        {props.context !== 'wowcast' && <SwipeInteraction containerRef={containerRef} />}

        {/* After the swipe divider, which shares its level of the stacking
            order, so the popup gets the clicks where the two overlap. */}
        <LayerInfoPopup
          clickInfo={clickInfo}
          activeObjectIndex={activeObjectIndex}
          handlePrevClick={handlePrevClick}
          handleNextClick={handleNextClick}
          onClose={() => setClickInfo(null)}
        />

        {props.outerChildren}
      </Box>
    </Box>
  );
};

export default Map;

const LoadingIndicator = (props: { status: 'loading' | 'init' | 'loaded' }) => {
  if (props.status !== 'loading') {
    return null;
  }

  return (
    <CircularProgress
      sx={{
        position: 'absolute',
        left: 'calc(50% - 20px)',
        top: 'calc(50% - 20px)',
        zIndex: 3,
      }}
      color="primary"
    />
  );
};

type MapButtonsProps = {
  miniMap?: boolean;
  context?: string;
  title: string;
  portalRef: React.RefObject<HTMLDivElement | null>;
  mapLoaded: boolean;
  deckRef: React.RefObject<DeckGLRef | null>;
  mapRef: React.RefObject<MapRef | null>;
};

const MapButtons = React.memo((props: MapButtonsProps) => {
  const portalTarget = props.portalRef.current;
  if (!portalTarget) return null;

  return createPortal(
    <Box
      data-print="hidden"
      sx={{
        width: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        flexDirection: 'row',
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 3,
        pointerEvents: 'auto',

        '> *': {
          margin: '0 0.5rem 0.5rem 0',
        },

        '@media (max-height: 800px)': {
          flexDirection: 'row',

          '> *': {
            margin: '0 0.5rem 0 0',
          },
        },
      }}
    >
      <DownloadButton
        title={props.title}
        ready={props.mapLoaded}
        deckRef={props.deckRef}
        mapRef={props.mapRef}
      />
      <ToggleLabelsButton />
    </Box>,
    portalTarget
  );
});

type MapNavigationProps = {
  portalRef: React.RefObject<HTMLDivElement | null>;
};

const MapNavigation = React.memo((props: MapNavigationProps) => {
  const portalTarget = props.portalRef.current;
  if (!portalTarget) return null;

  return createPortal(
    <Box
      data-print="hidden"
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 3,
        pointerEvents: 'auto',
      }}
    >
      <NavigationButtons />
    </Box>,
    portalTarget
  );
});

type HandleLayersProps = {
  children?: React.ReactNode;
  mapStyle: string | null;
  selectedMap: PrimaryLayer;
  mapRef?: React.RefObject<MapRef | null>;
  deckRef?: React.RefObject<DeckGLRef | null>;
  context?: string;
  zoomLevel: 'inner' | 'outer';
};

type ChildProps = {
  layers: LayersList;
  status: 'loading' | 'init' | 'loaded';
};

const HandleLayers = React.memo((props: HandleLayersProps) => {
  // Shared layer-visibility / opacity / swipe state for the layer controls.
  const leafVisibility = useAtomValue(leafVisibilityAtom);
  const groupVisibility = useAtomValue(groupVisibilityAtom);
  const groupOpacity = useAtomValue(groupOpacityAtom);
  const leafOpacity = useAtomValue(leafOpacityAtom);
  const swipeEnabled = useAtomValue(swipeEnabledAtom);
  const swipeDrag = useAtomValue(swipeAtom);
  const swipeLayers = useAtomValue(swipeLayersAtom);
  const setPresentLeaves = useSetAtom(presentLeavesAtom);

  const countyLayer = useCountyLayer({
    autoHighlight: props.zoomLevel === 'outer',
  });

  const regionLayer = useRegionLayer({
    mode: props.context === 'wowcast' ? 'all' : 'select',
  });

  const homeLayers = useHomeLayers({
    selectedMap: props.selectedMap,
  });
  const { layers: incidentLayers, status } = useIncidentLayers();
  const structuresLayer = useStructuresLayer();
  const tribalLayer = useTribalLayer();

  const layers = React.useMemo(() => {
    return [
      countyLayer[0],
      regionLayer,
      ...homeLayers,
      ...incidentLayers,
      countyLayer[1],
      structuresLayer,
      tribalLayer,
    ];
  }, [countyLayer, regionLayer, homeLayers, incidentLayers, structuresLayer, tribalLayer]);

  // The deck.gl layer tree this component assembles is deeply nested
  // (arrays-of-arrays + null entries). Pull the child's own layers (e.g.
  // weather events) so they join the same merged tree, then the override step
  // flattens it before matching.
  const childLayers = React.useMemo(() => {
    const found: unknown[] = [];
    React.Children.forEach(props.children, child => {
      const item = child as ReactElement<PropsWithChildren<ChildProps>>;
      if (React.isValidElement(item) && item.props.layers != null) {
        found.push(item.props.layers);
      }
    });
    return found;
  }, [props.children]);

  const rawMerged = React.useMemo(() => [...childLayers, ...layers], [childLayers, layers]);

  // Present leaves = leaf keys with at least one RAW layer that is `visible`.
  // Computed from the flattened raw tree BEFORE the overrides; set in an
  // effect (never write an atom during render).
  React.useEffect(() => {
    const present = computePresentLeaves(flattenLayers(rawMerged));
    setPresentLeaves(prev =>
      prev.length === present.length && prev.every((k, i) => k === present[i]) ? prev : present
    );
  }, [rawMerged, setPresentLeaves]);

  const childrenWithProps = React.Children.map<ReactNode, ReactNode>(props.children, child => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    const item = child as ReactElement<PropsWithChildren<ChildProps>>;
    if (React.isValidElement(item)) {
      if (item.props.layers != null) {
        // Apply the layer-control overrides as the LAST transform. The
        // override step recursively flattens the nested tree, so a flat
        // Layer[] reaches deck.
        return React.cloneElement(item, {
          status,
          layers: applyLayerOverrides(rawMerged, {
            leafVisibility,
            groupVisibility,
            groupOpacity,
            leafOpacity,
            swipe: { enabled: swipeEnabled, drag: swipeDrag, layers: swipeLayers },
          }),
        });
      } else {
        return React.cloneElement(item, { status });
      }
    }

    return child;
  });

  return <>{childrenWithProps}</>;
});
